/**
 * Attribution Helper — First-touch UTM capture + persistence
 *
 * Contratto chiave: `sai_attribution_first_touch`
 * Shape: { source, medium, campaign, content, referrer, landed_at }
 * - source/medium/campaign/content: da utm_* query params (null se assente)
 * - referrer: document.referrer (null se assente)
 * - landed_at: ISO string (new Date().toISOString())
 *
 * FIRST-TOUCH WINS: se la chiave esiste già, non sovrascrive.
 * Never-throw: wrappato in try/catch, non rompe mai il render.
 *
 * @module utils/attribution
 */

export const ATTRIBUTION_KEY = 'sai_attribution_first_touch';

/**
 * Legge i parametri UTM dall'URL corrente.
 * @returns {{ source: string|null, medium: string|null, campaign: string|null, content: string|null }}
 */
const readUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get('utm_source') || null,
    medium: params.get('utm_medium') || null,
    campaign: params.get('utm_campaign') || null,
    content: params.get('utm_content') || null,
  };
};

/**
 * Cattura il first-touch attribution e lo persiste in localStorage.
 * FIRST-TOUCH WINS: se la chiave esiste già, esce senza sovrascrivere.
 * Chiamabile su qualsiasi pagina (/welcome, landing SEO, ecc.).
 *
 * @returns {Object|null} L'oggetto attribution salvato, o null se già presente / errore
 */
export const captureFirstTouch = () => {
  try {
    // First-touch wins: se già presente, non sovrascrivere
    const existing = localStorage.getItem(ATTRIBUTION_KEY);
    if (existing) return null;

    const { source, medium, campaign, content } = readUtmParams();
    const referrer = document.referrer || null;

    const attribution = {
      source,
      medium,
      campaign,
      content,
      referrer,
      landed_at: new Date().toISOString(),
    };

    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    console.log('[Attribution] First-touch captured:', attribution);
    return attribution;
  } catch (e) {
    // Silenzioso: non deve mai rompere il render
    console.warn('[Attribution] captureFirstTouch failed (non-blocking):', e);
    return null;
  }
};

/**
 * Legge l'attribution salvata in localStorage.
 * @returns {Object|null}
 */
export const getStoredAttribution = () => {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};
